// src/adminFlow/AdminEditProductPage.js
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';
import API_BASE from '../apiBase';
import { ArrowLeft, Home } from 'lucide-react';

import { broadcastInvalidation } from './adminInvalidation';

function Field({ label, hint, required, children }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-gray-700">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      {children}
      {hint ? <div className="text-xs text-gray-500">{hint}</div> : null}
    </div>
  );
}

export default function AdminEditProductPage() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const initialVid = Number(search.get('v') || 0);
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // product-level
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('active');
  const [currency, setCurrency] = useState('CAD');
  const [imageUrl, setImageUrl] = useState('');
  const [flavorsText, setFlavorsText] = useState('');

  // dietary flags
  const [isSugarFree, setIsSugarFree] = useState(false);
  const [isVegan, setIsVegan] = useState(false);
  const [isGlutenFree, setIsGlutenFree] = useState(false);
  const [isAllNatural, setIsAllNatural] = useState(false);

  // variants
  const [variants, setVariants] = useState([]);
  const [variantId, setVariantId] = useState(null);

  // editable variant fields
  const [casePack, setCasePack] = useState('Case');
  const [caseUnits, setCaseUnits] = useState('');
  const [casePrice, setCasePrice] = useState('');
  const [caseSku, setCaseSku] = useState('');
  const [caseStock, setCaseStock] = useState('');

  const selectedVariant = useMemo(
    () => variants.find((v) => v.id === variantId) || null,
    [variants, variantId]
  );

  function fillFromVariant(v) {
    if (!v) return;
    setCasePack(v.pack_size ?? 'Case');
    setCaseUnits(v.unit_count ?? '');
    setCasePrice(v.price ?? '');
    setCaseSku(v.sku ?? '');
    setCaseStock(v.stock_qty ?? '');
  }

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/admin/products/${id}`, {
          credentials: 'include',
          cache: 'no-store',
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || 'Load failed');

        const p = j.product;
        setName(p.name || '');
        setBrand(p.brand || '');
        setCategory(p.category || '');
        setDescription(p.description || '');
        setStatus(p.status || 'active');
        setCurrency(p.currency || 'CAD');
        setImageUrl(p.image_url || '');
        setFlavorsText((j.flavors || []).join(', '));

        // dietary flags
        setIsSugarFree(!!p.is_sugar_free);
        setIsVegan(!!p.is_vegan);
        setIsGlutenFree(!!p.is_gluten_free);
        setIsAllNatural(!!p.is_all_natural);

        const vs = j.variants || [];
        setVariants(vs);

        const vid =
          initialVid && vs.some((v) => v.id === initialVid) ? initialVid : vs[0]?.id ?? null;
        setVariantId(vid);
        fillFromVariant(vs.find((v) => v.id === vid));
      } catch (e) {
        setErr(e.message || 'Load error');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, initialVid]);

  function onChangeVariant(vidStr) {
    const vid = Number(vidStr);
    setVariantId(vid);
    const v = variants.find((vv) => vv.id === vid);
    fillFromVariant(v);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr('');
    try {
      const payload = {
        name,
        brand,
        category,
        description,
        status,
        currency,
        image_url: imageUrl,
        flavors: flavorsText.split(',').map((s) => s.trim()).filter(Boolean),

        // dietary flags
        is_sugar_free: !!isSugarFree,
        is_vegan: !!isVegan,
        is_gluten_free: !!isGlutenFree,
        is_all_natural: !!isAllNatural,

        // tell backend which variant to update
        variant_id: variantId,
        case_pack_size: casePack,
        case_unit_count: caseUnits,
        case_price: casePrice,
        case_sku: caseSku,
        case_stock: caseStock,
      };

      const r = await fetch(`${API_BASE}/api/admin/products/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Update failed');

      // notify other admin tabs to refresh immediately
      broadcastInvalidation('products:changed', { id: Number(id) });

      nav('/admin/products', { replace: true });
    } catch (e) {
      setErr(e.message || 'Update error');
    }
  }

  if (loading) return <div className="p-8">Loading…</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Edit Product</h1>
          <div className="flex gap-2">
            <Link
              to="/admin"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Home className="w-4 h-4" /> Dashboard
            </Link>
            <Link
              to="/admin/products"
              className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Inventory
            </Link>
          </div>
        </div>

        {err && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-800 border border-red-100">
            {err}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Product basics */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" required>
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field label="Brand">
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                />
              </Field>
              <Field label="Category">
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                />
              </Field>
              <Field label="Currency">
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="Description">
                  <textarea
                    rows={3}
                    className="w-full border rounded-lg px-3 py-2"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Field>
              </div>
              <Field label="Status">
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="active">active</option>
                  <option value="hidden">hidden</option>
                  <option value="discontinued">discontinued</option>
                </select>
              </Field>

              {/* Dietary & Claims */}
              <div className="md:col-span-2">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Dietary & Claims</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <label className="inline-flex items-center gap-2 p-2 rounded hover:bg-gray-50 border border-gray-200">
                    <input
                      type="checkbox"
                      checked={isSugarFree}
                      onChange={(e) => setIsSugarFree(e.target.checked)}
                    />
                    <span className="text-sm text-gray-700">Sugar-Free</span>
                  </label>
                  <label className="inline-flex items-center gap-2 p-2 rounded hover:bg-gray-50 border border-gray-200">
                    <input
                      type="checkbox"
                      checked={isVegan}
                      onChange={(e) => setIsVegan(e.target.checked)}
                    />
                    <span className="text-sm text-gray-700">Vegan</span>
                  </label>
                  <label className="inline-flex items-center gap-2 p-2 rounded hover:bg-gray-50 border border-gray-200">
                    <input
                      type="checkbox"
                      checked={isGlutenFree}
                      onChange={(e) => setIsGlutenFree(e.target.checked)}
                    />
                    <span className="text-sm text-gray-700">Gluten-Free</span>
                  </label>
                  <label className="inline-flex items-center gap-2 p-2 rounded hover:bg-gray-50 border border-gray-200">
                    <input
                      type="checkbox"
                      checked={isAllNatural}
                      onChange={(e) => setIsAllNatural(e.target.checked)}
                    />
                    <span className="text-sm text-gray-700">All-Natural</span>
                  </label>
                </div>
              </div>

              <Field label="Image URL">
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                />
              </Field>
              <Field label="Flavors" hint="Comma-separated">
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={flavorsText}
                  onChange={(e) => setFlavorsText(e.target.value)}
                />
              </Field>
            </div>
          </section>

          {/* Variant editor */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="mb-3">
              <Field label="Variant">
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  value={variantId ?? ''}
                  onChange={(e) => onChangeVariant(e.target.value)}
                >
                  {variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {(v.pack_size || 'Case') + ' — ' + (v.sku || '')}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <h3 className="font-semibold mb-3 text-gray-800">
              Variant (Case)
              {selectedVariant ? ` — ${selectedVariant.pack_size} (${selectedVariant.sku})` : ''}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Case label">
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={casePack}
                  onChange={(e) => setCasePack(e.target.value)}
                />
              </Field>
              <Field label="Cans per case">
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="w-full border rounded-lg px-3 py-2"
                  value={caseUnits}
                  onChange={(e) => setCaseUnits(e.target.value)}
                />
              </Field>
              <Field label="Price">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full border rounded-lg px-3 py-2"
                  value={casePrice}
                  onChange={(e) => setCasePrice(e.target.value)}
                />
              </Field>
              <Field label="Stock (cases)">
                <input
                  type="number"
                  step="1"
                  min="0"
                  className="w-full border rounded-lg px-3 py-2"
                  value={caseStock}
                  onChange={(e) => setCaseStock(e.target.value)}
                />
              </Field>
              <Field label="SKU">
                <input
                  className="w-full border rounded-lg px-3 py-2"
                  value={caseSku}
                  onChange={(e) => setCaseSku(e.target.value)}
                />
              </Field>
            </div>
          </section>

          <div className="flex items-center justify-end gap-3">
            <Link
              to="/admin/products"
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              className="px-5 py-2 rounded-lg bg-orange-600 text-white hover:bg-orange-700"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
